import { useContext, useEffect, useState } from 'react';
import {
    Accordion,
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Row,
    Col,
    Button,
    Card,
    CardBody,
    Badge,
    Label,
} from 'reactstrap';
import { useFormContext } from 'react-hook-form';
import { renderIcon, BlueHorizontalLine } from '~/shared/components/icons';
import { getDashColor } from '~/shared/components/icons';
import { PayableTo } from './PayableTo';
import Expenses from './Expenses';
import Notes from './Notes';
import NotesPanel from '../../components/NotesPanel';
import { useHistory, useParams } from 'react-router';
import { AdminContext } from '~/context/admin';
import {
    fetchFundRequest,
    fetchVendorOptions,
    populateVendors,
    fetchVendorDetail,
} from '~/services/fundrequests';
import { FundRequest, TransactionDefault } from '~/types/fundrequests';
import { Patient } from '~/types/campaign';
import moment from 'moment';
import { FaEye } from 'react-icons/fa';
import hhlAdminIcon from '~/shared/img/hhl-admin-icon.png';
import RequiredInfoAlert from './RequiredInfoAlert';
import { FundRequestContext } from '~/context/fundrequests';
import toast from 'react-hot-toast';
import { CampaignContext } from '~/context/campaign';
import { getBadgeLabel, getBadgeClass } from '~/shared/utils';
import VendorImportModal from './VendorImportModal';

const getStatusColor = (status: string | undefined): string => {
    if (!status) {
        return '#e0e0e0';
    }
    switch (status.toLowerCase()) {
        case 'needs_info':
            return '#9c3353';
        case 'for_dof_approval':
            return '#f78154';
        case 'submitted':
            return '#25b7b5';
        case 'in_review':
            return '#febf3e';
        case 'approved':
            return '#92c353';
        case 'paid':
            return '#1e1e1e';
        case 'resubmitted':
            return '#7cd5f3';
        case 'rejected':
            return '#f02d3a';
        default:
            return '#e0e0e0';
    }
};

const editabledStatuses = ['draft', 'needs_info', 'submitted'];

const getFundDisbursementGuidelinesUrl = (
    campaignType: string | undefined
): string => {
    if (campaignType?.toLowerCase() === 'transplant') {
        return 'https://s3.amazonaws.com/helphopelive/media/forms_n_flyers/9_Fund_Disbursement_Guidelines_-_Transplant_hJCo4kR.pdf';
    }
    // Default to Injury/Illness for all other cases including 'injury/illness' or undefined
    return 'https://s3.amazonaws.com/helphopelive/media/forms_n_flyers/9_Fund_Disbursement_Guidelines_-_Injury_and_Illness_kUNuE5y.pdf';
};

// Helper function to determine campaign type from campaign patient data
const getCampaignTypeFromPatient = (
    patient: Patient | null | undefined
): string => {
    // If patient has transplant-specific fields populated, it's likely a transplant campaign
    if (
        patient?.transplant_diagnosis ||
        patient?.candidacy_status ||
        patient?.listing_date ||
        patient?.living_donor
    ) {
        return 'transplant';
    }
    // Default to injury/illness for all other cases
    return 'injury/illness';
};

export const SubmitRequest = ({
    handleSubmission,
    setShowConfirmationPage,
}) => {
    const {
        formState: { isSubmitting, isValid },
        reset,
        getValues,
        watch,
    } = useFormContext();
    const [{ fundRequest }, fundRequestDispatch] =
        useContext(FundRequestContext);
    const [{ campaign }, campaignDispatch] = useContext(CampaignContext);
    const { fundrequest_id } = useParams<string | null>();
    const history = useHistory();
    const [{ isAdmin, campaignId }, adminDispatch] = useContext(AdminContext);

    const [expenseItemsHasFlags, setExpenseItemsHasFlags] = useState(false);
    const [open, setOpen] = useState<string[]>(['1']);
    const [isImportingVendors, setIsImportingVendors] = useState(false);
    const [showNotes, setShowNotes] = useState(false);

    // Loading states to coordinate both fund request and vendor loads
    const [isLoadingFundRequest, setIsLoadingFundRequest] = useState(false);
    const [isLoadingVendors, setIsLoadingVendors] = useState(false);
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [
        vendorDetailLoadedInCoordinatedLoad,
        setVendorDetailLoadedInCoordinatedLoad,
    ] = useState(false);

    // Watch for ACH agreement and payment type
    const paymentType = watch('payment_type');
    const achAgreementSigned = watch('ach_info.agreement_signed');
    const achAccountType = watch('ach_info.account_type');
    const achDocumentType = watch('ach_info.document_type');
    const toggle = (id: string) => {
        if (open.includes(id)) {
            setOpen(open.filter((item) => item !== id));
        } else {
            setOpen([...open, id]);
        }
    };
    function checkUploadsPresence(fundRequest) {
        if (!fundRequest) return false;
        return Object.values(fundRequest)
            .filter((value) => Array.isArray(value))
            .some((transactions) =>
                transactions.some(
                    (transaction) => transaction.uploads?.length > 0
                )
            );
    }

    const isExpanded = (id: string) => {
        if (fundrequest_id) {
            return true;
        }
        return open.includes(id);
    };

    const checkExpenseItemsHasFlags = (data: FundRequest) => {
        let flagged = false;
        const {
            transactions_fundraising,
            transactions_medical,
            transactions_medications,
            transactions_insurance,
            transactions_relocation_lodging,
            transactions_other,
            transactions_physical_therapy,
            transactions_caregiving,
        } = data;
        const transactions_default = [
            transactions_fundraising || [],
            transactions_medical || [],
            transactions_medications || [],
            transactions_insurance || [],
            transactions_relocation_lodging || [],
            transactions_other || [],
            transactions_physical_therapy || [],
            transactions_caregiving || [],
        ]
            .flat()
            .filter(Boolean);
        transactions_default.forEach((item) => {
            if (item && item.flagged_comment) {
                flagged = true;
            }
        });
        (data.transactions_food || []).forEach((item) => {
            if (item && item.flagged_comment) {
                flagged = true;
            }
        });
        (data.transactions_travel || []).forEach((item) => {
            if (item && item.flagged_comment) {
                flagged = true;
            }
        });
        setExpenseItemsHasFlags(flagged);
    };

    const getVendors = async (campaignId: number) => {
        try {
            setIsLoadingVendors(true);
            const resp = await fetchVendorOptions(campaignId);
            if (resp.error) {
                throw resp.error;
            }
            fundRequestDispatch({
                type: 'set_vendors',
                payload: resp,
            });
        } catch (error) {
            console.log('error: ', error);
        } finally {
            setIsLoadingVendors(false);
        }
    };

    const handleAutoVendorImport = async (campaignId: number) => {
        try {
            setIsImportingVendors(true);
            setIsLoadingVendors(true);
            await populateVendors(campaignId.toString());
            // Fetch vendors after import
            await getVendors(campaignId);
        } catch (error) {
            console.log('vendor import error: ', error);
            toast.error('Failed to import vendors. Please try again.');
            setIsLoadingVendors(false);
        } finally {
            setIsImportingVendors(false);
        }
    };

    useEffect(() => {
        if (!fundRequest) return;
        if (fundRequest.detail) {
            // Assume invalid token and redirect to login
            history.push('/login');
        }

        // Store the current form values before reset
        const currentFormValues = getValues();
        const currentACHInfo = currentFormValues?.ach_info;
        const currentNote = currentFormValues?.note;

        // Capture all current expense items from the form
        const currentFundraisingExpenses =
            currentFormValues?.transactions_fundraising || [];
        const currentMedicalExpenses =
            currentFormValues?.transactions_medical || [];
        const currentMedicationsExpenses =
            currentFormValues?.transactions_medications || [];
        const currentInsuranceExpenses =
            currentFormValues?.transactions_insurance || [];
        const currentRelocationExpenses =
            currentFormValues?.transactions_relocation_lodging || [];
        const currentOtherExpenses =
            currentFormValues?.transactions_other || [];
        const currentTravelExpenses =
            currentFormValues?.transactions_travel || [];
        const currentFoodExpenses = currentFormValues?.transactions_food || [];
        const currentPhysicalTherapyExpenses =
            currentFormValues?.transactions_physical_therapy || [];
        const currentCaregivingExpenses =
            currentFormValues?.transactions_caregiving || [];

        const {
            id,
            vendor,
            vendor_address,
            campaign,
            transactions_fundraising,
            transactions_medical,
            transactions_medications,
            transactions_insurance,
            transactions_relocation_lodging,
            transactions_other,
            transactions_travel,
            transactions_food,
            transactions_physical_therapy,
            transactions_caregiving,
            notes,
            document_id,
            fund_request_status,
            needs_ach_info,
            needs_info_note,
            payment_type,
        } = fundRequest;

        // Helper function to merge expense items, preserving local changes
        const mergeExpenseItems = (serverItems = [], localItems = []) => {
            if (!serverItems || !localItems) return serverItems;

            // Create a map of items by id for quick lookup
            const localItemsMap = localItems.reduce((map, item) => {
                // Only consider items with id as they exist on server
                if (item && item.id) {
                    map[item.id] = item;
                }
                return map;
            }, {});

            // Merge server items with locally modified ones
            return serverItems.map((serverItem) => {
                if (
                    serverItem &&
                    serverItem.id &&
                    localItemsMap[serverItem.id]
                ) {
                    // Preserve uploads from server for consistent reference
                    const serverUploads = serverItem.uploads || [];
                    const localItem = localItemsMap[serverItem.id];

                    // Merge the local changes with server item
                    return {
                        ...serverItem,
                        ...localItem,
                        // Ensure uploads are always from server
                        uploads: serverUploads,
                        // Preserve existing_uploads if they exist locally
                        existing_uploads: localItem.existing_uploads || [],
                    };
                }
                return serverItem;
            });
        };

        const fundRequestValues = {
            id,
            vendor: vendor?.id || '',
            transactions_fundraising: mergeExpenseItems(
                transactions_fundraising,
                currentFundraisingExpenses
            ),
            transactions_medical: mergeExpenseItems(
                transactions_medical,
                currentMedicalExpenses
            ),
            transactions_medications: mergeExpenseItems(
                transactions_medications,
                currentMedicationsExpenses
            ),
            transactions_insurance: mergeExpenseItems(
                transactions_insurance,
                currentInsuranceExpenses
            ),
            transactions_relocation_lodging: mergeExpenseItems(
                transactions_relocation_lodging,
                currentRelocationExpenses
            ),
            transactions_other: mergeExpenseItems(
                transactions_other,
                currentOtherExpenses
            ),
            transactions_travel: mergeExpenseItems(
                transactions_travel,
                currentTravelExpenses
            ),
            transactions_food: mergeExpenseItems(
                transactions_food,
                currentFoodExpenses
            ),
            transactions_physical_therapy: mergeExpenseItems(
                transactions_physical_therapy,
                currentPhysicalTherapyExpenses
            ),
            transactions_caregiving: mergeExpenseItems(
                transactions_caregiving,
                currentCaregivingExpenses
            ),
            note: currentNote || '',
            document_id,
            fund_request_status: fund_request_status.value,
            needs_ach_info,
            needs_info_note,
            payment_type,
            vendor_address: vendor_address?.id,
        };

        // If ACH info exists in current form values, preserve it through the reset
        if (currentACHInfo) {
            fundRequestValues.ach_info = currentACHInfo;
        } else if (fundRequest.ach_request) {
            // Transform ach_request from the API to ach_info for the form
            // Store the full URL as 'path' so backend knows it's already a complete URL
            fundRequestValues.ach_info = {
                account_type: fundRequest.ach_request.account_type,
                document_type: fundRequest.ach_request.document_type,
                agreement_signed: fundRequest.ach_request.agreement_signed,
                filename: fundRequest.ach_request.ach_document_filename,
                path: fundRequest.ach_request.ach_document_url, // Store URL as path - backend will detect it's already a full URL
            };
        }

        reset(fundRequestValues);

        checkExpenseItemsHasFlags(fundRequest);
        if (
            editabledStatuses.indexOf(fundRequest.fund_request_status.value) ===
            -1
        ) {
            setShowConfirmationPage(true);
        }
    }, [fundRequest]);

    const getFundRequest = async (fundrequest_id: string) => {
        try {
            setIsLoadingFundRequest(true);
            setShowConfirmationPage(false);
            // fetch fund request
            const resp = await fetchFundRequest(fundrequest_id, campaignId);

            if (resp.error) {
                throw resp.error;
            }

            fundRequestDispatch({
                type: 'set_fund_request',
                payload: resp,
            });

            return resp; // Return the response so we can use it for vendor detail loading
        } catch (error) {
            console.log('error: ', error);
            if (typeof error === 'string') {
                toast.error(error);
            } else {
                toast.error('Something went wrong. Please try again.');
            }
            throw error; // Re-throw so caller knows it failed
        } finally {
            setIsLoadingFundRequest(false);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            if (fundrequest_id) {
                // Loading existing fund request
                setOpen(['1', '2', '3']);

                const campaignId = campaign?.campaignDetail?.campaign_id;
                if (!campaignId) return;

                try {
                    // Start both fund request and vendor list loads
                    const fundRequestPromise = getFundRequest(fundrequest_id);

                    let vendorPromise = Promise.resolve();
                    // Check if vendors need to be synced
                    const mipVendorsSyncedDate =
                        campaign?.campaignDetail?.mip_vendors_synced_date;
                    const ntafId = campaign?.campaignDetail?.ntaf_id;

                    if (!mipVendorsSyncedDate && ntafId) {
                        // Auto import vendors if they haven't been synced and campaign has NTAF ID
                        vendorPromise = handleAutoVendorImport(campaignId);
                    } else {
                        // Just fetch existing vendors
                        vendorPromise = getVendors(campaignId);
                    }

                    // Wait for both to complete, and get the fund request data
                    const [fundRequestData] = await Promise.all([
                        fundRequestPromise,
                        vendorPromise,
                    ]);

                    // If the fund request has a vendor, load its details to prevent UI stagger
                    if (fundRequestData?.vendor?.id) {
                        setIsLoadingVendors(true);
                        try {
                            const vendorDetail = await fetchVendorDetail(
                                fundRequestData.vendor.id,
                                campaignId
                            );
                            fundRequestDispatch({
                                type: 'set_selected_payee',
                                payload: vendorDetail,
                            });
                            setVendorDetailLoadedInCoordinatedLoad(true);
                        } catch (error) {
                            console.log('Error loading vendor detail:', error);
                        } finally {
                            setIsLoadingVendors(false);
                        }
                    }
                } catch (error) {
                    console.log('Error in coordinated loading:', error);
                }
            } else {
                // Creating new fund request
                setOpen(['1']);
                const campaignId = campaign?.campaignDetail?.campaign_id;
                if (campaignId) {
                    // Check if vendors need to be synced
                    const mipVendorsSyncedDate =
                        campaign?.campaignDetail?.mip_vendors_synced_date;
                    const ntafId = campaign?.campaignDetail?.ntaf_id;

                    if (!mipVendorsSyncedDate && ntafId) {
                        // Auto import vendors if they haven't been synced and campaign has NTAF ID
                        await handleAutoVendorImport(campaignId);
                    } else {
                        // Just fetch existing vendors
                        await getVendors(campaignId);
                    }
                }
            }

            setInitialLoadComplete(true);
        };

        loadData();
    }, [fundrequest_id, campaign?.campaignDetail]);

    const lastUpdated = fundRequest?.updated_date
        ? moment(fundRequest.updated_date).format('MM/DD/YY')
        : '';

    const getButtonTextWrtStatus = (status: string) => {
        switch (status) {
            case 'draft':
                return 'Next';

            case 'needs_info':
                return 'Resubmit';

            default:
                return 'Save';
        }
    };

    // Check if ACH info form is being displayed
    const isAchInfoFormShown = () => {
        const fundRequestStatus = fundRequest?.fund_request_status?.value;
        return (
            fundRequest?.needs_ach_info &&
            fundRequestStatus === 'needs_info' &&
            paymentType === 'ACH'
        );
    };

    // Show loading until initial load is complete
    if (!initialLoadComplete || (fundrequest_id && !fundRequest)) {
        return <div>Loading...</div>;
    }

    const statusValue = fundRequest?.fund_request_status.value;
    const statusLabel = fundRequest?.fund_request_status.label;
    let badgeLabel = getBadgeLabel(statusValue, statusLabel);
    const badgeClass = getBadgeClass(badgeLabel, statusValue);

    return (
        <>
            <VendorImportModal isOpen={isImportingVendors} />
            <div className='mb-3'>
                <h1 className='h2'>
                    {fundRequest?.fund_request_status?.value === 'needs_info'
                        ? 'Resubmit Request'
                        : 'Submit Request'}
                </h1>
                <div className='d-flex align-items-center flex-wrap'>
                    <Badge
                        color='none'
                        className={`${badgeClass} me-3`}
                        // style={{
                        //     backgroundColor: `${getStatusColor(
                        //         fundRequest?.fund_request_status.value
                        //     )}`,
                        // }}
                    >
                        {badgeLabel}
                    </Badge>
                    {fundrequest_id ? (
                        <span className='text-muted me-3'>
                            Request ID{' '}
                            <span className='fw-bold'>{fundrequest_id}</span>
                        </span>
                    ) : (
                        ''
                    )}
                    <span className='text-muted'>
                        {lastUpdated ? `Last Updated ${lastUpdated}` : ''}
                    </span>
                </div>
            </div>
            {fundRequest?.fund_request_status?.value === 'needs_info' && (
                <RequiredInfoAlert
                    expenseItemsHasFlags={expenseItemsHasFlags}
                />
            )}
            <div>
                <Row>
                    <Col lg={8}>
                        {/* Fund Disbursement Guidelines moved above Payee section */}
                        <Card
                            className='mt-4'
                            style={{
                                background: getDashColor('supporters'),
                            }}
                        >
                            <CardBody>
                                <ul>
                                    <li>
                                        See{' '}
                                        <a
                                            href={getFundDisbursementGuidelinesUrl(
                                                getCampaignTypeFromPatient(
                                                    campaign?.patient
                                                )
                                            )}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                        >
                                            Fund Disbursement Guidelines
                                        </a>{' '}
                                        for a list of eligible expenses and
                                        required documentation.
                                    </li>
                                    <li>
                                        Requests for reimbursement must include
                                        a bill/invoice/statement and proof of
                                        payment.
                                    </li>
                                    <li>
                                        Requests for direct payments must have
                                        the full and current bill/invoice.
                                    </li>
                                    <li>
                                        Separate fund request forms must be
                                        completed for each direct pay
                                        bill/invoice/statement.
                                    </li>
                                    <li>
                                        Requests will be processed within 5-8
                                        business days of receipt of all required
                                        documentation.
                                    </li>
                                </ul>
                            </CardBody>
                        </Card>

                        <Accordion open={open} toggle={toggle}>
                            <AccordionItem>
                                <AccordionHeader
                                    targetId='1'
                                    className='less-round-accordion-header'
                                >
                                    Payee (Payable To)
                                </AccordionHeader>
                                <AccordionBody accordionId='1'>
                                    <PayableTo
                                        initialLoadComplete={
                                            initialLoadComplete
                                        }
                                    />
                                </AccordionBody>
                            </AccordionItem>
                            <Expenses
                                isExpanded={isExpanded}
                                handleSubmission={handleSubmission}
                            />
                            {/* Notes section moved to right column */}
                        </Accordion>
                    </Col>
                    <Col lg={4}>
                        <Card
                            className='mt-4'
                            style={{
                                background: '#ffffff',
                                boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
                                borderRadius: 12,
                            }}
                        >
                            <CardBody>
                                {fundRequest?.id ? (
                                    <NotesPanel />
                                ) : (
                                    <>
                                        <Label style={{ fontSize: '1rem' }}>
                                            Notes
                                        </Label>
                                        <BlueHorizontalLine />
                                        <div className='mt-3'>
                                            <Notes />
                                        </div>
                                    </>
                                )}
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
            <div className='sticky-bottom'>
                <div
                    style={{
                        display: 'flex',
                        gap: '12px',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                    }}
                >
                    {fundRequest?.fund_request_status?.value === 'draft' && (
                        <Button
                            type='submit'
                            color='link'
                            className='px-0'
                            onClick={(e) => {
                                handleSubmission({ draft: true });
                            }}
                        >
                            Save Draft
                        </Button>
                    )}
                    <div>
                        <Button
                            onClick={() => history.push('/requests')}
                            color='outline-dark'
                        >
                            Cancel
                        </Button>
                        <Button
                            color='primary'
                            disabled={isSubmitting || !isValid}
                            onClick={(e) => {
                                if (!checkUploadsPresence(fundRequest)) {
                                    toast.error(
                                        'Please upload at least one supporting document to your fund request'
                                    );
                                    return;
                                }
                                // Check if ACH form is shown and validate all required fields
                                if (isAchInfoFormShown()) {
                                    if (!achAccountType) {
                                        toast.error(
                                            'Please select an account type'
                                        );
                                        return;
                                    }
                                    if (!achDocumentType) {
                                        toast.error(
                                            'Please select a document type'
                                        );
                                        return;
                                    }
                                    if (!achAgreementSigned) {
                                        toast.error(
                                            'Please agree to the Terms and Conditions for Direct Deposit Payments before submitting'
                                        );
                                        return;
                                    }
                                }
                                handleSubmission();
                            }}
                        >
                            {isSubmitting
                                ? 'Saving...'
                                : getButtonTextWrtStatus(
                                      fundRequest?.fund_request_status?.value
                                  )}
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};
